// PipelineRunner — Run / Re-run every phase after the user has completed
// Phase 1 via the Phase Briefing Panel. Plan §M2.4/§M4.1-5.
//
// Each phase card renders:
//   * A Run / Re-run button with live status (blocked / ready / running
//     / complete) derived from the `/runs/{n}/artifacts` index.
//   * When complete, an inline preview of the phase's key artifacts so
//     the reviewer can verify output *before* moving to the next phase.
//     Each artifact row offers an "Open" link into the Artifact Viewer
//     (raw file) and an expandable in-card preview.
//
// UX v0.7 (plan §P1.7) — the legacy shadcn ``<Card>`` shell has been
// migrated to the canonical ``alm-*`` design language so the Pipeline
// surface no longer feels like a different app from the gates that
// surround it. Each phase row is now an ``alm-card`` with the same
// chip vocabulary the gates use, and the Run/Re-run button uses the
// shared ``PrimaryButton`` / ``SecondaryButton`` shell primitives.
//
// Plan §P1.11 — re-running a *completed* phase now opens a styled
// confirmation dialog (no more silent destructive click). Phase 3 in
// particular costs ~$0.90 per accidental click (model-dependent;
// rough order of magnitude — the active model is reported in the
// per-phase briefing card) — so the dialog explicitly calls out the
// cost when the user is about to kick the agents off again.

import { useMutation, useQuery } from '@tanstack/react-query'
import {
  CheckCircle2,
  CircleDashed,
  ExternalLink,
  FileText,
  Loader2,
  PackageOpen,
  PlayCircle,
  Sparkles,
  WandSparkles,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  PrimaryButton,
  SecondaryButton,
  Section,
} from '@/components/ui/shell'
import { api, ApiError, projectFileUrl } from '@/lib/api'
import { waitForPhaseCompletion } from '@/lib/phaseEvents'
import { humanBytes, runFolder } from '@/lib/format'
import { useUxStore } from '@/stores/uxStore'
import { getShape } from '@/components/intake/shapes'

// Number-word humaniser used for the Phase-3 description so the
// per-recipe roster reads as cleanly as the legacy "Five agents …"
// copy. Falls back to the bare numeral once we exceed the recipes
// we ship today (max ~7 agents).
const NUMBER_WORDS: Record<number, string> = {
  1: 'One',
  2: 'Two',
  3: 'Three',
  4: 'Four',
  5: 'Five',
  6: 'Six',
  7: 'Seven',
  8: 'Eight',
}

function humanizeAgentCount(n: number): string {
  return NUMBER_WORDS[n] ?? String(n)
}

// Strict linear flow (2026-07-02, owner-simplified). Whether a phase may
// run is NOT re-derived here — the parent (CommandCenterPage) owns the
// one flow ladder and tells us which single phase is the current step
// via ``runnablePhase``. A phase's Run button is enabled only when it IS
// that step. "Phases are just run; gates gate the next step" — there are
// no prior-gate lists, gate fetches, or visibility mirrors in the runner.


interface ArtifactIndexFile {
  path: string
  name: string
  size_bytes: number
  phase: number
}


interface ArtifactIndex {
  files: ArtifactIndexFile[]
}


interface PhaseSpec {
  phase: 1 | 2 | 3 | 4 | 5
  label: string
  description: string
  icon: typeof PlayCircle
  durationHint: string
  /** Files whose presence marks the phase as complete. */
  produces: string[]
  /** Files that must exist before this phase can run. */
  requires: string[]
  /**
   * Human-friendly preview targets: which artifacts to render inline
   * when the phase completes, in which order, and how to label them.
   */
  previewTargets: Array<{
    name: string
    label: string
    kind: 'markdown' | 'json' | 'group'
  }>
  /**
   * Phase-3 / Phase-4 are the costly re-runs. Surface a dollar hint in
   * the confirmation dialog so the architect understands what an
   * accidental click is about to spend.
   */
  rerunCostHint?: string
}


const PHASES: readonly PhaseSpec[] = [
  {
    phase: 1,
    label: 'Phase 1 — Contextualist',
    description:
      'Restates the problem, builds the goal hierarchy, and seeds the glossary.',
    icon: PlayCircle,
    durationHint: '~45–60 s · xhigh effort',
    produces: ['project-charter.md', 'intent-map.md', 'glossary.md'],
    requires: [],
    previewTargets: [
      { name: 'project-charter.md', label: 'Project Charter', kind: 'markdown' },
      { name: 'intent-map.md', label: 'Intent Map', kind: 'markdown' },
      { name: 'glossary.md', label: 'Glossary (seed)', kind: 'markdown' },
    ],
    // PR-T.4 — provider-agnostic cost hints. Pre-PR-T.4 each hint
    // read "Opus" verbatim and lied to anyone running on Gemini /
    // OpenAI. The actual model + cost ships in the per-phase
    // briefing card (see PhaseBriefingPanel + phase_briefings.py).
    rerunCostHint: '~$0.30 per re-run (see the phase briefing for the active model)',
  },
  {
    phase: 2,
    label: 'Phase 2 — Observer',
    description:
      'Classify every uploaded artifact, extract constraints with confidence scores, and write the Constraint Log + Document Map.',
    icon: PlayCircle,
    durationHint: '~1–2 min · medium effort',
    produces: ['constraint-log.json', 'document-map.md'],
    requires: ['project-charter.md'],
    previewTargets: [
      { name: 'constraint-log.json', label: 'Constraint Log', kind: 'json' },
      { name: 'document-map.md', label: 'Document Map', kind: 'markdown' },
    ],
    rerunCostHint: '~$0.20 per re-run (see the phase briefing for the active model)',
  },
  {
    phase: 3,
    label: 'Phase 3 — Generation Swarm',
    description:
      'Five agents (BA / System Architect / Safety / Diagrammer / UI Sketcher) run in parallel to produce BRD, SRD, compliance, diagrams, and wireframes.',
    icon: Sparkles,
    durationHint: '~5–10 min · 5 agents in parallel',
    produces: ['brd.md', 'srd.md', 'compliance-risks.md'],
    requires: ['constraint-log.json'],
    previewTargets: [
      { name: 'brd.md', label: 'Business Requirements (BRD)', kind: 'markdown' },
      { name: 'srd.md', label: 'System Requirements (SRD)', kind: 'markdown' },
      { name: 'compliance-risks.md', label: 'Compliance & Risk', kind: 'markdown' },
      { name: 'diagrams', label: 'Diagrams', kind: 'group' },
      { name: 'wireframes', label: 'Wireframes', kind: 'group' },
    ],
    rerunCostHint: '~$0.90 per re-run (5–7 agents in parallel)',
  },
  {
    phase: 4,
    label: 'Phase 4 — Critic',
    description:
      'Deterministic audit: EARS conformance + INCOSE 42 + traceability + provenance. Produces audit-diff.md + rqs.json and proposes REW-### rewrites.',
    icon: WandSparkles,
    durationHint: '~15 s · no LLM (rule-based)',
    produces: ['audit-diff.md', 'rqs.json'],
    requires: ['brd.md', 'srd.md'],
    previewTargets: [
      { name: 'rqs.json', label: 'Quality Score (RQS)', kind: 'json' },
      { name: 'audit-diff.md', label: 'Audit Diff & rewrites', kind: 'markdown' },
    ],
    rerunCostHint: 'Free — Phase 4 is deterministic',
  },
  {
    phase: 5,
    label: 'Phase 5 — Exporter',
    description:
      'Deterministic assembly of requirements.md / .pdf / traceability.csv + export.zip + audit-package.zip from the frozen template spec.',
    icon: PackageOpen,
    durationHint: '~10–30 s · no LLM',
    produces: ['requirements.md', 'export.zip'],
    requires: ['brd.md', 'srd.md'],
    previewTargets: [
      { name: 'requirements.md', label: 'Final Requirements Package', kind: 'markdown' },
      { name: 'misfits.json', label: 'Template mis-fits (G5 queue)', kind: 'json' },
    ],
    rerunCostHint: 'Free — Phase 5 is deterministic',
  },
]


function filenameSet(index: ArtifactIndex | undefined): Set<string> {
  const names = new Set<string>()
  for (const f of index?.files ?? []) names.add(f.name)
  return names
}


function buttonIcon(state: 'pending' | 'ready' | 'running' | 'done' | 'blocked') {
  if (state === 'running') return Loader2
  if (state === 'done') return CheckCircle2
  if (state === 'blocked') return CircleDashed
  return PlayCircle
}


/** Pull the first N chars of a file as a plain-text preview. */
function ArtifactPreview({
  projectId,
  relPath,
  maxChars = 1200,
}: {
  projectId: string
  relPath: string
  maxChars?: number
}) {
  const q = useQuery({
    queryKey: ['preview', projectId, relPath],
    queryFn: async () => {
      const res = await fetch(projectFileUrl(projectId, relPath))
      if (!res.ok) return ''
      return await res.text()
    },
    retry: false,
  })
  if (q.isLoading) {
    return (
      <p className="text-[11px] text-slate-500">Loading preview…</p>
    )
  }
  const body = q.data ?? ''
  if (!body) {
    return (
      <p className="text-[11px] text-slate-500">
        (Preview unavailable — open the file directly to inspect.)
      </p>
    )
  }
  // Strip leading YAML frontmatter so the preview focuses on prose.
  let stripped = body
  if (stripped.startsWith('---')) {
    const end = stripped.indexOf('\n---', 3)
    if (end > 0) stripped = stripped.slice(end + 4).trimStart()
  }
  const truncated =
    stripped.length > maxChars
      ? stripped.slice(0, maxChars).trimEnd() + '\n…'
      : stripped

  return (
    <pre
      className="mt-2 max-h-72 overflow-auto rounded bg-slate-950/40 border border-slate-700/50 p-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words text-slate-300"
      aria-label={`${relPath} preview`}
    >
      {truncated}
    </pre>
  )
}


/** Row showing a single artifact produced by a completed phase. */
function ArtifactRow({
  projectId,
  runId,
  file,
  label,
  kind,
}: {
  projectId: string
  runId: number
  file: ArtifactIndexFile
  label: string
  kind: 'markdown' | 'json' | 'group'
}) {
  const [expanded, setExpanded] = useState(false)
  const url = projectFileUrl(projectId, file.path)
  const folder = runFolder(runId)
  const isPreviewable = kind === 'markdown' || kind === 'json'
  return (
    <div className="rounded-md border border-slate-700/60 bg-slate-900/40">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-3.5 w-3.5 text-slate-500 shrink-0" aria-hidden />
          <span className="text-xs font-medium text-slate-200 truncate">{label}</span>
          <span className="alm-chip text-[10px] font-mono">{file.name}</span>
          <span className="text-[10px] text-slate-500 tabular-nums">
            {humanBytes(file.size_bytes)}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isPreviewable ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="text-[11px] text-slate-300 hover:text-slate-100"
            >
              {expanded ? 'Hide preview' : 'Preview'}
            </button>
          ) : null}
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-indigo-300 hover:underline"
            title={`Open ${file.path} in a new tab`}
          >
            <ExternalLink className="h-3 w-3" aria-hidden />
            Open
          </a>
        </div>
      </div>
      {expanded && isPreviewable ? (
        <div className="px-3 pb-3">
          <ArtifactPreview projectId={projectId} relPath={file.path} />
          <p className="mt-1 text-[10px] text-slate-500">
            Full file at{' '}
            <code className="font-mono">
              {folder}/…/{file.name}
            </code>{' '}
            — or switch to the Artifact Viewer below for rich rendering.
          </p>
        </div>
      ) : null}
    </div>
  )
}


/** Compact directory group (diagrams / wireframes) inside a phase card. */
function ArtifactGroupRow({
  projectId,
  index,
  groupName,
  label,
}: {
  projectId: string
  index: ArtifactIndex
  groupName: 'diagrams' | 'wireframes'
  label: string
}) {
  // Hooks must run before any early return to satisfy
  // react-hooks/rules-of-hooks; the empty-group check below
  // bails *after* the state declaration.
  const [expanded, setExpanded] = useState(false)
  const filtered = index.files.filter((f) =>
    f.path.split('/').includes(groupName),
  )
  if (filtered.length === 0) return null
  return (
    <div className="rounded-md border border-slate-700/60 bg-slate-900/40">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-800/30"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="h-3.5 w-3.5 text-slate-500 shrink-0" aria-hidden />
          <span className="text-xs font-medium text-slate-200">{label}</span>
          <span className="alm-chip text-[10px]">
            {filtered.length} file{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
        <span className="text-[11px] text-slate-500">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>
      {expanded ? (
        <ul className="px-3 pb-2 space-y-1">
          {filtered.map((f) => (
            <li
              key={f.path}
              className="flex items-center justify-between gap-2 text-[11px] text-slate-300"
            >
              <code className="font-mono truncate">{f.name}</code>
              <a
                href={projectFileUrl(projectId, f.path)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-indigo-300 hover:underline"
              >
                <ExternalLink className="h-3 w-3" aria-hidden /> Open
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}


// --------------------------------------------------------------------------- //
// Phase status chip — single source of truth for the "done / running /
// ready / blocked" vocabulary the audit flagged was inconsistent across
// surfaces (plan §P0.4).
// --------------------------------------------------------------------------- //
function PhaseStatusChip({
  phase,
  state,
}: {
  phase: 1 | 2 | 3 | 4 | 5
  state: 'pending' | 'ready' | 'running' | 'done' | 'blocked'
}) {
  // E2E hook (T2 — plan §5.5 / check-testids.mjs). One status pill
  // per phase row; the ``data-state`` attribute lets specs assert
  // ``done`` / ``running`` / ``ready`` / ``blocked`` declaratively
  // without scraping the visible label. The testid is intentionally
  // inlined as a template literal on every branch so the audit
  // script's regex can find each call site without an AST walk.
  if (state === 'done') {
    return (
      <span
        data-testid={`phase-${phase}-status`}
        data-state={state}
        className="alm-chip alm-chip-ok"
      >
        <CheckCircle2 className="h-3 w-3" /> done
      </span>
    )
  }
  if (state === 'running') {
    return (
      <span
        data-testid={`phase-${phase}-status`}
        data-state={state}
        className="alm-chip alm-chip-info"
      >
        <Loader2 className="h-3 w-3 animate-spin" /> running
      </span>
    )
  }
  if (state === 'ready') {
    return (
      <span
        data-testid={`phase-${phase}-status`}
        data-state={state}
        className="alm-chip alm-chip-info"
      >
        ready
      </span>
    )
  }
  return (
    <span
      data-testid={`phase-${phase}-status`}
      data-state={state}
      className="alm-chip"
    >
      <CircleDashed className="h-3 w-3" /> blocked
    </span>
  )
}


export function PipelineRunner({
  projectId,
  runId,
  runnablePhase = null,
}: {
  projectId: string
  runId: number
  /**
   * The single phase (2–5) whose turn it is per the parent's linear
   * flow ladder, or ``null`` when a gate must be answered first. A
   * phase's Run button is enabled only when ``spec.phase ===
   * runnablePhase``. All ordering / gate logic lives in the parent; the
   * runner just runs.
   */
  runnablePhase?: 2 | 3 | 4 | 5 | null
}) {
  // Re-run confirmation state. We don't open the dialog for first-run
  // (state === 'ready') — only when the user is about to overwrite an
  // already-completed phase. Plan §P1.11.
  const [pendingRerun, setPendingRerun] = useState<PhaseSpec | null>(null)

  const index = useQuery<ArtifactIndex>({
    queryKey: ['artifact-index', projectId, runId],
    queryFn: async () => {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(projectId)}/runs/${runId}/artifacts`,
      )
      if (!res.ok) throw new Error(`index fetch failed: ${res.status}`)
      return (await res.json()) as ArtifactIndex
    },
    retry: false,
    refetchInterval: 5000,
  })

  // Run-state record gives us the rewrite-count target the Phase 4
  // route writes BEFORE the Rewriter pre-pass fires (via
  // ``record_rqs`` in ``api/routes/phases.py``). Combined with the
  // count of ``phase4/rewrites/F-*.md`` files in the artifact index
  // it lets us render a live "Critic ✓ · materializing rewrites
  // (N/M)…" progress line while Phase 4 is the long-tail Rewriter
  // pre-pass. The query is cheap (single small JSON) and shares its
  // cache key with the CommandCenterPage's identical fetch, so we
  // pay only one network call total.
  const runState = useQuery({
    queryKey: ['run-state', projectId, runId],
    queryFn: () => api.getRunState(projectId, runId),
    retry: false,
    refetchInterval: 5000,
  })

  // Phase 2-5 routes now return 202 immediately (``?async=true``).
  // ``runPhaseN`` resolves the moment the backend accepts the job;
  // we then await ``phase_completed`` (or ``error``) on the WS stream
  // before considering the mutation successful. The legacy synchronous
  // POST held the HTTP connection open for 5-10 minutes which Chrome
  // would silently kill around the 5-min mark — see plan + comments in
  // ``src/ai_alm/api/routes/phases.py`` (async-phase plumbing).
  //
  // ``waitForPhaseCompletion`` listens on the phase-event bus that
  // ``useProjectEvents`` fans every WS event into. Subscribing AFTER
  // the POST is intentionally safe: the backend publishes
  // ``phase_started`` first, then runs the body, then publishes
  // ``phase_completed`` — so even a fast phase (< 1s) won't beat the
  // listener that runs inside the same microtask continuation.
  const phase2 = useMutation({
    mutationFn: async () => {
      await api.runPhase2(projectId, runId, {})
      return waitForPhaseCompletion(projectId, runId, 2)
    },
    onSuccess: () => index.refetch(),
  })
  const phase3 = useMutation({
    mutationFn: async () => {
      await api.runPhase3(projectId, runId)
      return waitForPhaseCompletion(projectId, runId, 3)
    },
    onSuccess: () => index.refetch(),
  })
  const phase4 = useMutation({
    mutationFn: async () => {
      await api.runPhase4(projectId, runId)
      return waitForPhaseCompletion(projectId, runId, 4)
    },
    onSuccess: () => index.refetch(),
  })
  const phase5 = useMutation({
    mutationFn: async () => {
      await api.runPhase5(projectId, runId)
      return waitForPhaseCompletion(projectId, runId, 5)
    },
    onSuccess: () => index.refetch(),
  })

  const mutations = useMemo(
    () =>
      ({
        2: phase2,
        3: phase3,
        4: phase4,
        5: phase5,
      }) as const,
    [phase2, phase3, phase4, phase5],
  )

  // While ANY phase is executing, downstream phase runs are not
  // clickable — the pipeline is in flux (artifacts appear mid-swarm),
  // so a Run/Re-run click now would race the in-flight phase. The
  // running phase shows its own ``running`` state; every other Run
  // button is disabled until it settles.
  const anyPhaseRunning = [phase2, phase3, phase4, phase5].some(
    (m) => m.isPending,
  )

  const names = filenameSet(index.data)
  const fileByName = useMemo(() => {
    const m = new Map<string, ArtifactIndexFile>()
    for (const f of index.data?.files ?? []) {
      if (!m.has(f.name)) m.set(f.name, f)
    }
    return m
  }, [index.data])

  // UX v0.7: Phase 3's agent roster is no longer fixed — it depends on
  // the project shape that was selected during Smart Intake (and stored
  // client-side in the UX store). The legacy hardcoded "Five agents
  // (BA / SA / Safety / Diagrammer / UI Sketcher)" copy was misleading
  // for shapes like `robotics_edge` which now ship 6 agents (adds
  // Security Architect) — see brain/15-workflow-recipes.md and
  // frontend/src/components/intake/shapes.ts.
  //
  // Compute a per-recipe description and duration hint here so each
  // Phase-3 card mirrors the agent badges shown on the intake screen.
  // For legacy projects without a saved shape we fall back to the
  // generic "Generation Swarm" wording rather than asserting a fixed
  // count.
  const uxState = useUxStore((s) => s.get(projectId))
  const phase3Spec = useMemo(() => {
    if (!uxState.shape) {
      return {
        description:
          'Recipe-driven swarm of specialist agents (BA / System Architect / Diagrammer / UI Sketcher / domain specialists) running in parallel to produce BRD, SRD, compliance, diagrams, and wireframes.',
        durationHint: '~5–10 min · agents in parallel · width adapts to recipe',
      }
    }
    const shape = getShape(uxState.shape)
    const agents = shape.agents
    const word = humanizeAgentCount(agents.length)
    const description = `${word} agents (${agents.join(
      ' / ',
    )}) run in parallel under the "${shape.recipe}" recipe to produce BRD, SRD, compliance, diagrams, and wireframes.`
    const durationHint = `~5–10 min · ${agents.length} agent${
      agents.length === 1 ? '' : 's'
    } in parallel`
    return { description, durationHint }
  }, [uxState.shape])

  // Click handler for the Run / Re-run button. Opens the confirm
  // dialog when re-running a completed phase; passes through directly
  // for first runs.
  const handleRunClick = (
    spec: PhaseSpec,
    state: 'pending' | 'ready' | 'running' | 'done' | 'blocked',
  ) => {
    if (spec.phase === 1) return
    const mutation = mutations[spec.phase as 2 | 3 | 4 | 5]
    if (state === 'done') {
      setPendingRerun(spec)
      return
    }
    mutation.mutate()
  }

  const confirmRerun = () => {
    if (!pendingRerun || pendingRerun.phase === 1) return
    const mutation = mutations[pendingRerun.phase as 2 | 3 | 4 | 5]
    mutation.mutate()
    setPendingRerun(null)
  }

  return (
    <div className="alm-fadein">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="alm-chip alm-chip-info">
              <PlayCircle className="h-3 w-3" /> Pipeline
            </span>
            <span className="alm-chip">Run #{runId}</span>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-100">
            Pipeline Runner
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Run Phases 2–5 in order. Each card turns <strong>done</strong>{' '}
            once the expected artifacts land on disk, and lists the outputs
            inline so you can verify and decide before moving on (plan §M4).
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {PHASES.map((spec) => {
          const mutation =
            spec.phase === 1 ? null : mutations[spec.phase as 2 | 3 | 4 | 5]
          const produced = spec.produces.every((n) => names.has(n))
          const canRun = spec.requires.every((n) => names.has(n))
          const state: 'pending' | 'ready' | 'running' | 'done' | 'blocked' =
            mutation?.isPending
              ? 'running'
              : produced
              ? 'done'
              : canRun
              ? 'ready'
              : 'blocked'
          // Strict flow: this phase is runnable only when it IS the
          // current step per the parent's ladder. If its inputs are on
          // disk but it's not its turn (an earlier gate is still open),
          // show it as ``blocked`` and disable the button — no gate
          // logic here, the parent already decided.
          const isRunnableNow =
            spec.phase !== 1 && spec.phase === runnablePhase
          const displayState =
            state === 'ready' && !isRunnableNow ? 'blocked' : state
          const Icon = buttonIcon(displayState)
          const errorMsg =
            mutation?.error instanceof ApiError
              ? mutation.error.message
              : mutation?.error
              ? String(mutation.error)
              : null

          const previews =
            state === 'done'
              ? spec.previewTargets
                  .map((t) => ({ target: t, file: fileByName.get(t.name) }))
                  .filter((row) => row.target.kind === 'group' || row.file)
              : []

          // Phase 4 long-tail progress: once the Critic has written
          // ``audit-diff.md`` the route enters the Rewriter pre-pass
          // (one LLM call per requirement, ~5-10 min for a typical
          // run). The "running" pill alone makes this look stuck, so
          // when we detect the post-Critic / pre-G4 window we render
          // a sub-line counting rewrite files in the artifact index
          // against the target count the Critic recorded on the
          // run-state. Denominator falls back to "?" if run-state
          // hasn't refreshed yet.
          const phase4Progress = (() => {
            if (spec.phase !== 4 || state !== 'running') return null
            const criticDone = names.has('audit-diff.md')
            if (!criticDone) return null
            const target = runState.data?.rqs?.rewrite_count ?? 0
            const done = (index.data?.files ?? []).reduce(
              (n, f) =>
                f.path.includes('phase4/rewrites/') &&
                f.name.startsWith('F-') &&
                f.name.endsWith('.md')
                  ? n + 1
                  : n,
              0,
            )
            return { done, target }
          })()

          return (
            <div key={spec.phase} className="alm-card space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <spec.icon
                    className="mt-0.5 h-4 w-4 text-slate-400 shrink-0"
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-slate-100">
                        {spec.label}
                      </h3>
                      <PhaseStatusChip phase={spec.phase} state={displayState} />
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {spec.phase === 3 ? phase3Spec.description : spec.description}
                    </p>
                    <p className="text-[11px] text-slate-500 mt-1 font-mono">
                      {spec.phase === 3
                        ? phase3Spec.durationHint
                        : spec.durationHint}
                    </p>
                  </div>
                </div>
                {/* Phase 1 is driven from the Phase Briefing Panel above
                    — here we show its status only, no Run button. */}
                {mutation ? (
                  state === 'done' ? (
                    <SecondaryButton
                      size="sm"
                      data-testid={`phase-${spec.phase}-run-button`}
                      data-state={state}
                      onClick={() => handleRunClick(spec, state)}
                      disabled={anyPhaseRunning}
                      icon={<Icon className="h-4 w-4" />}
                      title={
                        anyPhaseRunning
                          ? 'Wait for the running phase to finish'
                          : spec.rerunCostHint
                          ? `Re-run Phase ${spec.phase} — ${spec.rerunCostHint}`
                          : undefined
                      }
                    >
                      Re-run Phase {spec.phase}
                    </SecondaryButton>
                  ) : (
                    <PrimaryButton
                      data-testid={`phase-${spec.phase}-run-button`}
                      data-state={state}
                      onClick={() => handleRunClick(spec, state)}
                      loading={state === 'running'}
                      disabled={
                        state === 'running' || !isRunnableNow || anyPhaseRunning
                      }
                      icon={<Icon className="h-4 w-4" />}
                      title={
                        anyPhaseRunning && state !== 'running'
                          ? 'Wait for the running phase to finish'
                          : state === 'blocked'
                          ? `Blocked: needs ${spec.requires.join(', ')}`
                          : !isRunnableNow
                          ? 'Finish the current step on the timeline first'
                          : undefined
                      }
                    >
                      Run Phase {spec.phase}
                    </PrimaryButton>
                  )
                ) : (
                  <span className="alm-chip">Run from briefing panel above</span>
                )}
              </div>

              {state === 'blocked' ? (
                <p className="text-[11px] text-slate-500">
                  Needs <code className="font-mono">{spec.requires.join(', ')}</code>{' '}
                  from upstream phases before it can run.
                </p>
              ) : displayState === 'blocked' ? (
                <p className="text-[11px] text-slate-500">
                  Not this phase's turn yet — finish the current step on the
                  timeline (left) first.
                </p>
              ) : null}

              {phase4Progress ? (
                <div
                  className="rounded-md border border-indigo-700/40 bg-indigo-950/20 px-3 py-2 text-[12px] text-indigo-200 flex items-start gap-2"
                  data-testid="phase-4-rewriter-progress"
                >
                  <Loader2 className="h-3.5 w-3.5 mt-0.5 shrink-0 animate-spin" />
                  <div className="min-w-0">
                    <div>
                      <span className="text-emerald-300">Critic ✓</span>
                      <span className="text-indigo-400/70 mx-1.5">·</span>
                      Materialising LLM rewrites{' '}
                      <span className="font-mono text-indigo-100">
                        {phase4Progress.done}
                        {phase4Progress.target > 0
                          ? ` / ${phase4Progress.target}`
                          : ''}
                      </span>
                      …
                    </div>
                    <div className="text-[10.5px] text-indigo-300/70 mt-0.5">
                      One LLM call per requirement before G4 emits — typically
                      5–10 min. Files land in{' '}
                      <code className="font-mono">phase4/rewrites/</code> as
                      they complete.
                    </div>
                  </div>
                </div>
              ) : null}

              {errorMsg ? (
                state === 'done' ? (
                  // Artifacts landed on disk despite the failed HTTP
                  // response — almost always the "long-running route +
                  // browser/proxy timed out the fetch" case (Phase 4
                  // takes 5-10 min when the Rewriter pre-pass fires).
                  // Don't claim the phase failed; the work completed.
                  // Surface a softer advisory so the architect knows
                  // the connection dropped but can still proceed.
                  <Alert variant="warning">
                    <AlertTitle>Connection dropped — outputs ready</AlertTitle>
                    <AlertDescription>
                      The browser lost the HTTP response while Phase{' '}
                      {spec.phase} was still running, but the server
                      finished and wrote the artifacts below. You can
                      proceed.{' '}
                      <span className="opacity-70">
                        (Underlying error: {errorMsg})
                      </span>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive">
                    <AlertTitle>Phase {spec.phase} failed</AlertTitle>
                    <AlertDescription>{errorMsg}</AlertDescription>
                  </Alert>
                )
              ) : null}

              {previews.length > 0 && index.data ? (
                <Section
                  label="Outputs — verify before moving on"
                  className="mb-0 mt-2"
                >
                  <div className="space-y-1.5">
                    {previews.map(({ target, file }) =>
                      target.kind === 'group' ? (
                        <ArtifactGroupRow
                          key={target.name}
                          projectId={projectId}
                          index={index.data!}
                          groupName={target.name as 'diagrams' | 'wireframes'}
                          label={target.label}
                        />
                      ) : file ? (
                        <ArtifactRow
                          key={target.name}
                          projectId={projectId}
                          runId={runId}
                          file={file}
                          label={target.label}
                          kind={target.kind}
                        />
                      ) : null,
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">
                    Happy with the output?{' '}
                    {spec.phase === 4 ? (
                      <>
                        Review <strong>Gate G4</strong> (left rail) to approve
                        rewrites, then move on to Phase 5.
                      </>
                    ) : spec.phase === 5 ? (
                      <>
                        Head to the <strong>Download Center</strong> to grab
                        the bundles, or review <strong>Gate G5</strong> to
                        confirm the final export.
                      </>
                    ) : spec.phase === 1 ? (
                      <>
                        Review <strong>Gate G1</strong> on the timeline rail
                        before running Phase 2.
                      </>
                    ) : (
                      <>Run the next phase.</>
                    )}{' '}
                    Not happy? Hit <em>Re-run</em> or use the Briefing Panel's{' '}
                    <em>Reject</em> control.
                  </p>
                </Section>
              ) : null}
            </div>
          )
        })}
      </div>

      <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 p-3 mt-4 flex gap-2 text-xs text-slate-400">
        <Sparkles className="h-3.5 w-3.5 text-indigo-400 shrink-0 mt-0.5" />
        <div>
          <strong className="text-slate-200">Tip:</strong> after every phase,
          the matching Gate Review panel on the left rail lets you{' '}
          <strong>Approve / Edit / Reject</strong> before the next phase
          consumes tokens.
        </div>
      </div>

      {/* Re-run confirmation dialog — guards against accidental
          destructive clicks on already-completed phases. The cost hint
          comes from the phase spec; Phase 3 explicitly calls out
          the ~$0.90 per click (model-dependent — the per-phase
          briefing carries the active model + provider). */}
      <ConfirmDialog
        open={pendingRerun !== null}
        title={`Re-run ${pendingRerun?.label ?? ''}?`}
        tone={pendingRerun && pendingRerun.phase === 3 ? 'danger' : 'warn'}
        confirmLabel={`Re-run Phase ${pendingRerun?.phase ?? ''}`}
        description={
          pendingRerun ? (
            <>
              <p>
                This will <strong>overwrite</strong> the existing artifacts
                from Phase {pendingRerun.phase} and restart the agents.
              </p>
              {pendingRerun.rerunCostHint ? (
                <p className="text-amber-200">
                  <strong>Cost:</strong> {pendingRerun.rerunCostHint}.
                </p>
              ) : null}
              <p className="text-xs text-slate-400">
                Downstream phases (and their gates) will need to be
                re-reviewed once this run finishes.
              </p>
            </>
          ) : null
        }
        onCancel={() => setPendingRerun(null)}
        onConfirm={confirmRerun}
      />
    </div>
  )
}
